export interface ApiClientOptions {
  baseUrl?: string;
  fetcher?: typeof fetch;
  defaultHeaders?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetcher: typeof fetch;
  private readonly defaultHeaders: Record<string, string>;
  private bearerToken: string | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
    this.fetcher = options.fetcher ?? fetch.bind(globalThis);
    this.defaultHeaders = options.defaultHeaders ?? {};
  }

  setBearerToken(token: string | null): void {
    this.bearerToken = token;
  }

  async get<T>(path: string): Promise<T> {
    const res = await this.fetcher(this.url(path), {
      method: 'GET',
      headers: this.headers(),
      credentials: 'include',
    });
    return this.parse<T>(res, path);
  }

  async post<TResponse, TBody = unknown>(path: string, body: TBody): Promise<TResponse> {
    const res = await this.fetcher(this.url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.headers() },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    return this.parse<TResponse>(res, path);
  }

  async patch<TResponse, TBody = unknown>(path: string, body: TBody): Promise<TResponse> {
    const res = await this.fetcher(this.url(path), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.headers() },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    return this.parse<TResponse>(res, path);
  }

  private headers(): Record<string, string> {
    const headers = { ...this.defaultHeaders };
    if (this.bearerToken) {
      headers['Authorization'] = `Bearer ${this.bearerToken}`;
    }
    return headers;
  }

  private url(path: string): string {
    return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private async parse<T>(res: Response, path: string): Promise<T> {
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ApiError(res.status, path, `Request to ${path} failed (${res.status}): ${text}`);
    }
    return res.json() as Promise<T>;
  }
}
